import { useState } from 'react';
import { CourseMarkdown } from '@/components/CourseMarkdown';
import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';
import { SiteFrame } from '@/pages/learn/playground/SiteFrame';
import example from './membership-example.json';
const completedFiles = example.files.map((f) => ({ ...f, kind: 'text' as const }));
/** Same product result renderer for public preview, member workspace and isolated Demo. */
export function MembershipOutcome() {
  const { t } = usePortalLocale();
  const [view, setView] = useState<'game' | 'report' | 'source'>('game');
  const [run, setRun] = useState(0);
  return (
    <section className="card-base" aria-labelledby="membership-result-title" data-membership-result>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow eyebrow-sky">{t('Complete result example')}</p>
          <h2 id="membership-result-title" className="mt-2 text-[24px] font-bold">
            {t('Star Sprint')}
          </h2>
          <p className="mt-2 text-sm text-ink-soft">
            {t(
              'Play the finished project and read its full parent report. Fictional learner · pre-generated work.',
            )}
          </p>
        </div>
        <span className="sticker-mint">{t('Ready to play')}</span>
      </div>
      <div className="my-4 flex flex-wrap gap-2" aria-label={t('Completed result views')}>
        {(['game', 'report', 'source'] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={view === v ? 'btn-pill-primary' : 'btn-pill-secondary'}
            aria-pressed={view === v}
            onClick={() => setView(v)}
          >
            {v === 'game'
              ? t('Play complete game')
              : v === 'report'
                ? t('Read full report')
                : t('View complete source')}
          </button>
        ))}
      </div>
      {view === 'game' ? (
        <>
          <div className="h-[880px] min-w-0 overflow-hidden rounded-3xl border border-hairline">
            <SiteFrame files={completedFiles} runKey={run} readOnly kind="website" />
          </div>
          <button
            type="button"
            className="btn-pill-ghost mt-3"
            onClick={() => setRun((r) => r + 1)}
          >
            {t('Reload original game')}
          </button>
        </>
      ) : view === 'report' ? (
        <CourseMarkdown text={example.report_md} />
      ) : (
        <div>
          <p className="mb-3 text-sm text-ink-soft">
            {t('The complete index.html behind the playable result.')}
          </p>
          <pre className="max-h-[600px] overflow-auto rounded-2xl bg-surface p-4 text-xs">
            <code>{example.files[0].content}</code>
          </pre>
        </div>
      )}
      <p className="mt-4 text-xs text-slate2">
        {t(
          'This example shows the product’s result format. It does not claim independent learner authorship, a live AI run, a guaranteed outcome or completion of a real course.',
        )}
      </p>
    </section>
  );
}
